import {reactive} from 'vue';
import {AxiosInstance} from 'axios';
import AuthService from '@core/services/AuthService';
import ApiService from '@core/services/ApiService';
import ConfigProvider from '@core/services/ConfigProvider';
import {useAdminStore} from '@core/store/AdminStore';
import {useUserStore} from '@core/store/UserStore';

export interface Modules {
  AzureAD: {
    enabled: boolean;
  };
  SubscriptionManager: {
    enabled: boolean;
  };
  InternalDeveloperPortal: {
    enabled: boolean;
  };
  Portfolio: {
    enabled: boolean;
  };
  DataDictionary: {
    enabled: boolean;
  };
  ExternalDeveloperPortal: {
    enabled: boolean;
  };
}

const defaultModules: Modules = {
  AzureAD: {enabled: false},
  SubscriptionManager: {enabled: false},
  InternalDeveloperPortal: {enabled: false},
  Portfolio: {enabled: false},
  DataDictionary: {enabled: false},
  ExternalDeveloperPortal: {enabled: false},
};

function mergeModulesPayload(data: unknown): Modules {
  if (!data || typeof data !== 'object') {
    return defaultModules;
  }
  return {...defaultModules, ...(data as Partial<Modules>)};
}

/** Role id as returned by the admin API (spelling matches backend contract). */
const PORTFOLIO_ROLES = [
  'admin',
  'architect',
  'productOwner',
  'businessAnalist',
  'readOnly',
] as const;

const DATA_DICTIONARY_ROLES = ['admin', 'businessAnalist', 'readOnly'] as const;

function rolesIncludeAny(roles: string[], allowed: readonly string[]): boolean {
  return allowed.some(r => roles.includes(r));
}

class ModulesService {
  /**
   * Reactive reference to the modules configuration.
   *
   * Kept reactive (and mutated in place rather than reassigned) so that Vue
   * components/computeds reading the `*Enabled` getters re-render when the
   * configuration is loaded after the layout has already mounted (e.g. after
   * an in-place login), instead of requiring a hard page refresh.
   */
  public modules: Modules = reactive<Modules>(structuredClone(defaultModules));

  /**
   * Cached roles to avoid redundant API calls.
   */
  private roles: string[] | null = null;

  /**
   * In-flight roles fetch so concurrent `getModules` calls share one request.
   */
  private rolesLoadPromise: Promise<void> | null = null;

  /**
   * Tracks whether the modules have been fetched.
   */
  private modulesFetched: boolean = false;

  /**
   * Returns the admin store instance.
   * @returns {ReturnType<typeof useAdminStore>}
   */
  private getAdminStore(): ReturnType<typeof useAdminStore> {
    return useAdminStore();
  }

  /**
   * Returns the user store instance.
   * @returns {ReturnType<typeof useUserStore>}
   */
  private getUserStore(): ReturnType<typeof useUserStore> {
    return useUserStore();
  }

  /**
   * Fetches and caches user roles if not already cached.
   * @returns {Promise<void>}
   */
  private async getRoles(): Promise<void> {
    if (this.roles) {
      return;
    }
    this.rolesLoadPromise ??= this.loadRolesInternal();
    await this.rolesLoadPromise;
  }

  private async loadRolesInternal(): Promise<void> {
    try {
      const userStore = this.getUserStore();
      if (
        Object.keys(userStore.user).length > 0 &&
        AuthService.isAuthenticated()
      ) {
        const user = await AuthService.getUser();
        if (user && !user.expired) {
          const adminStore = this.getAdminStore();
          this.roles = await adminStore.getRoles();
        }
      }
    } catch (error) {
      console.error('ModulesService: failed to load roles', error);
    } finally {
      this.rolesLoadPromise = null;
    }
  }

  /**
   * Returns the default API instance.
   * @returns {AxiosInstance}
   */
  private getApi(): AxiosInstance {
    return ApiService.getDefaultApiInstance();
  }

  /**
   * Returns true if the module is disabled in the configuration.
   * Environment variables in Vite are strings, so we check for 'false'.
   * @param {string} configName The name of the configuration setting.
   * @returns {boolean}
   */
  private isModuleDisabledByConfig(configName: string): boolean {
    const value = ConfigProvider.value(configName);
    return value === 'false' || value === false;
  }

  /**
   * Returns true if the Subscription Manager module is enabled and the user has the 'subscriptionManager' role.
   * @returns {boolean}
   */
  get subscriptionManagerEnabled(): boolean {
    if (this.isModuleDisabledByConfig('subscriptionManagerModuleEnabled')) {
      return false;
    }
    const roles = this.getAdminStore().roles;
    if (!roles) return false;
    return (
      this.modules.SubscriptionManager.enabled &&
      roles.includes('subscriptionManager')
    );
  }

  /**
   * Returns true if the Internal Developer Portal module is enabled.
   * @returns {boolean}
   */
  get internalDeveloperPortalEnabled(): boolean {
    return this.modules.InternalDeveloperPortal.enabled;
  }

  /**
   * Returns true if the Portfolio module is enabled and the user has one of the allowed roles.
   * @returns {boolean}
   */
  get portfolioEnabled(): boolean {
    if (this.isModuleDisabledByConfig('portfolioModuleEnabled')) {
      return false;
    }
    const roles = this.getAdminStore().roles;
    if (!roles) return false;
    return (
      this.modules.Portfolio.enabled && rolesIncludeAny(roles, PORTFOLIO_ROLES)
    );
  }

  /**
   * Returns true if the Data Dictionary module is enabled and the user has one of the allowed roles.
   * @returns {boolean}
   */
  get dataDictionaryEnabled(): boolean {
    if (this.isModuleDisabledByConfig('dataDictionaryModuleEnabled')) {
      return false;
    }
    const roles = this.getAdminStore().roles;
    if (!roles) return false;
    return (
      this.modules.DataDictionary.enabled &&
      rolesIncludeAny(roles, DATA_DICTIONARY_ROLES)
    );
  }

  /**
   * Returns true if the External Developer Portal module is enabled.
   * @returns {boolean}
   */
  get externalDeveloperPortalEnabled(): boolean {
    return this.modules.ExternalDeveloperPortal.enabled;
  }

  /**
   * Returns true if the AzureAD module is enabled.
   * @returns {boolean}
   */
  get azureADEnabled(): boolean {
    return (
      !this.isModuleDisabledByConfig('azureAdModuleEnabled') &&
      this.modules.AzureAD.enabled
    );
  }

  /**
   * Fetches the modules configuration from the API and updates the reference.
   * Prevents redundant API calls by checking the `modulesFetched` flag.
   * Also fetches user roles if not already cached.
   * @returns {Promise<Modules>} The modules configuration.
   */
  async getModules(fetchRoles: boolean = true): Promise<Modules> {
    const api = this.getApi();
    if (!this.modulesFetched) {
      try {
        const response = await api.get('/modules');
        Object.assign(this.modules, mergeModulesPayload(response.data));
        this.modulesFetched = true;
      } catch (error) {
        console.error('Error fetching modules:', error);
        Object.assign(this.modules, structuredClone(defaultModules));
        this.modulesFetched = false;
      }
    }
    if (fetchRoles) {
      await this.getRoles();
    }
    return this.modules;
  }
}

export default new ModulesService();
